import {
  ETIQUETA_CAMPO_EXPORTACION,
  ETIQUETA_CAMPO_EXPORTACION_EN,
  ETIQUETA_TIPO_EXPORTACION,
  ETIQUETA_TIPO_EXPORTACION_EN,
  TIPOS_DE_EXPORTACION,
  type CatalogosDeSeguimiento,
} from '@cnc/contracts';
import {et, tr, trx} from '../i18n/idioma';
import {ayudaDeCampo} from './ayuda-seguimiento';
import type {Campo} from './campos-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';

type Props = {
  campo: Campo;
  valor: string;
  cambiado?: boolean;
  catalogos: CatalogosDeSeguimiento | undefined;
  onCambiar: (valor: string) => void;
  // Sin etiqueta ni ayuda: solo el control (celdas y cambio en lote).
  compacta?: boolean;
  autoFoco?: boolean;
  onTecla?: (e: React.KeyboardEvent) => void;
  onSalir?: () => void;
};

// Un control de edicion para un campo de exportacion: lista para estado y
// tipo, Si/No para lo booleano, fecha, numero o texto con sugerencias.
export function EntradaDeCampo({
  campo,
  valor,
  cambiado,
  catalogos,
  onCambiar,
  compacta,
  autoFoco,
  onTecla,
  onSalir,
}: Props) {
  const etiqueta = et(ETIQUETA_CAMPO_EXPORTACION, ETIQUETA_CAMPO_EXPORTACION_EN)[campo.clave];
  const clase = compacta
    ? 'h-7 w-full rounded border border-verde bg-white px-1.5 text-sm text-tinta outline-none'
    : `mt-1 w-full rounded-md border px-2 py-1.5 text-sm text-tinta ${cambiado ? 'border-verde bg-verde-claro/40' : 'border-linea'}`;
  const listaId = campo.lista ? `lista-${campo.lista}` : undefined;
  const comunes = {
    value: valor,
    className: clase,
    autoFocus: autoFoco,
    onKeyDown: onTecla,
    onBlur: onSalir,
    'aria-label': etiqueta,
  };
  let control: React.ReactNode;
  if (campo.tipo === 'estado') {
    control = (
      <select {...comunes} onChange={e => onCambiar(e.target.value)}>
        {valor === '' && <option value="">{tr('Elige un estado', 'Choose a status')}</option>}
        {(catalogos?.estados ?? []).map(s => (
          <option key={s.codigo} value={s.codigo}>
            {s.etiqueta}
          </option>
        ))}
      </select>
    );
  } else if (campo.tipo === 'tipo') {
    control = (
      <select {...comunes} onChange={e => onCambiar(e.target.value)}>
        {valor === '' && <option value="">{tr('Elige un tipo', 'Choose a type')}</option>}
        {TIPOS_DE_EXPORTACION.map(t => (
          <option key={t} value={t}>
            {et(ETIQUETA_TIPO_EXPORTACION, ETIQUETA_TIPO_EXPORTACION_EN)[t]}
          </option>
        ))}
      </select>
    );
  } else if (campo.tipo === 'booleano') {
    control = (
      <select {...comunes} onChange={e => onCambiar(e.target.value)}>
        <option value="">{tr('Sin dato', 'No data')}</option>
        <option value="si">{tr('Sí', 'Yes')}</option>
        <option value="no">No</option>
      </select>
    );
  } else if (campo.clave === 'notas' && !compacta) {
    control = <textarea {...comunes} onChange={e => onCambiar(e.target.value)} rows={3} />;
  } else {
    control = (
      <>
        <input
          {...comunes}
          type={
            campo.tipo === 'fecha' ? 'date' : campo.tipo === 'numero' || campo.tipo === 'entero' ? 'number' : 'text'
          }
          step={campo.tipo === 'numero' ? 'any' : campo.tipo === 'entero' ? 1 : undefined}
          onChange={e => onCambiar(e.target.value)}
          list={listaId}
        />
        {campo.lista && catalogos && (
          <datalist id={listaId}>
            {(catalogos[campo.lista] as string[]).map(n => (
              <option key={n} value={n} />
            ))}
          </datalist>
        )}
      </>
    );
  }
  if (compacta) return control;
  const nota = ayudaDeCampo(campo.clave);
  return (
    <label className="block text-xs text-apagado">
      {etiqueta}
      {nota && <NotaDeAyuda titulo={etiqueta} texto={nota} dentroDeEtiqueta />}
      {control}
      {campo.ayuda && <span className="mt-0.5 block text-[11px] text-apagado">{trx(campo.ayuda)}</span>}
    </label>
  );
}
